import React, {useMemo, useState} from 'react';

import {Button, CheckIcon, CreateIcon, DeleteIcon, InputNumber, ModalPopup} from '@kit';

import {uuid} from '../../../../components/utils/uuid';
import {useCharacter} from '../character-context';
import {
	BASE_ITEM_CHOICE,
	EquipmentStack,
	isSeparate,
	isSeparateKind,
	setStackQuantity,
	stackEquipment,
	stackOffHand,
} from '../model/equipment';
import {ItemPickButton} from './item-search/item-pick-button';
import {ItemSearchList} from './item-search/item-search-list';
import type {SearchItem} from './item-search/use-item-search';

import css from './sheet.module.css';

type Props = {
	open: boolean;
	onClose(): void;
};

export const EquipmentManagement: React.FC<Props> = ({open, onClose}) =>
	open ? (
		<ModalPopup open onClose={onClose} header="Управление снаряжением" maxWidth={720}>
			<ItemSearch />
			<CurrentEquipment />
			<div className={css.modalButtons}>
				<Button appearance="primary" icon={<CheckIcon />} onClick={onClose}>
					Готово
				</Button>
			</div>
		</ModalPopup>
	) : null;

const ItemSearch: React.FC = () => {
	const {character, update} = useCharacter();
	const needsAttunement = (entryKey: string) =>
		!!character.equipment.find(({entry}) => entry.key === entryKey)?.item.requiresAttunement;

	/** доспех, щит и предмет с настройкой всегда новой записью, остальное прибавляется к стопке */
	const addItem = (item: SearchItem, itemId: string, baseItemId?: string) =>
		update((current) => {
			const existing = isSeparateKind({type: item.type, subTypes: item.subType || []})
				? undefined
				: current.equipment.find(
						(entry) =>
							entry.itemId === itemId &&
							!entry.offHand &&
							entry.choices?.[BASE_ITEM_CHOICE]?.[0] === baseItemId &&
							!needsAttunement(entry.key),
				  );
			return {
				equipment: existing
					? current.equipment.map((entry) =>
							entry === existing ? {...entry, quantity: entry.quantity + 1} : entry,
					  )
					: [
							...current.equipment,
							{
								key: uuid(),
								itemId,
								quantity: 1,
								...(baseItemId ? {choices: {[BASE_ITEM_CHOICE]: [baseItemId]}} : null),
							},
					  ],
			};
		});

	return (
		<ItemSearchList
			renderAction={(item) => (
				<ItemPickButton
					item={item}
					icon={<CreateIcon />}
					onPick={(itemId, baseItemId) => addItem(item, itemId, baseItemId)}
				>
					Добавить
				</ItemPickButton>
			)}
		/>
	);
};

const CurrentEquipment: React.FC = () => {
	const {character, update} = useCharacter();
	const stacks = stackEquipment(character.equipment, {byItem: true});
	if (!stacks.length) return null;

	const setQuantity = (stack: EquipmentStack, quantity: number) =>
		update((current) => ({equipment: setStackQuantity(current.equipment, stack, quantity)}));

	const removeStack = (stack: EquipmentStack) => {
		const keys = stack.entries.map(({entry}) => entry.key);
		update((current) => ({
			equipment: current.equipment.filter((entry) => !keys.includes(entry.key)),
			attunement: current.attunement.filter((entryKey) => !keys.includes(entryKey)),
		}));
	};

	return (
		<section className={css.tabSection}>
			<h3 className={css.groupTitle}>У персонажа</h3>
			<ul className={css.rows}>
				{stacks.map((stack) => (
					<li key={stack.key} className={css.row}>
						<div className={css.rowMain}>
							<span className={css.rowTitle}>{stack.item.title}</span>
							<div className={css.rowAside}>
								{!isSeparate(stack.item) && (
									<InputNumber
										className={css.quantityInput}
										min={Math.max(1, stackOffHand(stack))}
										value={stack.quantity}
										onChange={(quantity) =>
											setQuantity(stack, Math.max(1, stackOffHand(stack), quantity || 1))
										}
									/>
								)}
								<Button
									appearance="subtle"
									icon={<DeleteIcon />}
									aria-label="Убрать"
									onClick={() => removeStack(stack)}
								/>
							</div>
						</div>
					</li>
				))}
			</ul>
		</section>
	);
};
